import React, { useState } from "react";
import { toResult, type Result, type CheckStatus } from "@sequesign/verifier-browser";

import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";
import { sha256Prefixed } from "../../../../src/lib/hash.js";

// A distinct Overview LAYOUT for the content-provenance demo: a published news
// story with a C2PA-style "content credential" that expands into a verifiable
// provenance manifest (sourced -> drafted -> fact-checked -> editor sign-off ->
// published), the actions of ONE sealed receipt. Verification is honest to the
// protocol: the whole receipt is verified as a unit, so one "Verify the
// credential" click checks every step together. A toggle switches between two
// recorded receipts — a story an editor signed off, and one the agent
// auto-published; the missing-sign-off step is flagged the moment the
// auto-published story is selected, and the byline changes with it. All copy +
// the scenario->bundle wiring come from props.

export interface ProvStep {
  icon: "sources" | "draft" | "check" | "signoff" | "publish";
  label: string;
  detail: string;
  // When present, this step is the mandate breach (shown flagged before verify,
  // non-conformant after).
  flag?: string;
}

export interface NewsArticle {
  kicker: string;
  headline: string;
  // Byline rendered as structured parts (no raw HTML): "{lead} · {mid}", with
  // `mid` shown in the warn color when midWarn is set (the auto-published "no
  // editor review" annotation). Only sealed facts appear — the reviewer maps to
  // the sealed editor_ref; the "no editor review" note maps to signoff_status.
  byline: { lead: string; mid: string; midWarn?: boolean };
  lede: string[];
}

export interface NewsScenario {
  key: string;
  toggleLabel: string;
  // Key into the `bundles` map: the recorded content credential this shows.
  bundleId: string;
  article: NewsArticle;
  steps: ProvStep[];
  summaryTitle: string;
  summaryDetail: string;
  // false when this story breaches the editorial mandate (conformant:false).
  expectConformant: boolean;
}

export interface ContentCredentialConfig {
  eyebrow: string;
  headline: string;
  sub: string;
  credTitle: string;
  credSub: string;
  // The canonical serialization of the displayed article. The component
  // recomputes its sha256 and compares it to the credential's sealed
  // content_hash, so a "verified" verdict only claims the shown story is
  // authenticated when the bytes actually match.
  articleContent: string;
  mandate: {
    sourcesLabel: string;
    sources: string;
    signoffLabel: string;
    signoff: string;
  };
  scenarios: NewsScenario[];
  verifyLabel: string;
  verifyingLabel: string;
  reverifyLabel: string;
  browserNote: string;
}

export interface NewsCta {
  label: string;
  sub: string;
  onClick: () => void;
}

const STATUS_MARK: Record<CheckStatus, string> = {
  pass: "✓",
  fail: "✕",
  partial: "◐",
  not_evaluated: "–"
};

const ICONS: Record<ProvStep["icon"], React.ReactNode> = {
  sources: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 5h16M4 12h16M4 19h10" />
    </svg>
  ),
  draft: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  ),
  check: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  ),
  signoff: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 3l5 5-11 11H5v-5z" />
    </svg>
  ),
  publish: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11l18-7-7 18-2.5-7.5z" />
    </svg>
  )
};

export function ContentCredential({
  config,
  bundles,
  ctas
}: {
  config: ContentCredentialConfig;
  bundles: Record<string, Record<string, string>>;
  ctas: NewsCta[];
}) {
  const [active, setActive] = useState<string>(config.scenarios[0]?.key ?? "");
  const [results, setResults] = useState<Record<string, Result>>({});
  // Whether the displayed article's hash matches the credential's sealed
  // content_hash (keyed by bundleId). Kept alongside the verify result so the
  // verdict can only claim "the story you're reading is authenticated" when it
  // is actually true.
  const [contentMatches, setContentMatches] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<boolean>(false);

  const scenario = config.scenarios.find((s) => s.key === active) ?? config.scenarios[0];
  const result = results[scenario.bundleId] ?? null;
  const contentMatch =
    scenario.bundleId in contentMatches ? contentMatches[scenario.bundleId] : null;
  const error = errors[scenario.bundleId] || null;
  const verified = result !== null;

  // Recompute the displayed article's hash and compare it to the content_hash
  // sealed in the receipt's content_drafted evidence.
  function articleMatchesCredential(bundle: Record<string, string>): boolean {
    const draftKey = Object.keys(bundle).find((k) => k.includes("content-drafted"));
    if (!draftKey) return false;
    try {
      const ev = JSON.parse(bundle[draftKey]) as { content?: { content_hash?: unknown } };
      const sealed = ev.content?.content_hash;
      return typeof sealed === "string" && sealed === sha256Prefixed(config.articleContent);
    } catch {
      return false;
    }
  }

  async function verify(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setErrors((e) => ({ ...e, [scenario.bundleId]: "" }));
    try {
      const bundle = bundles[scenario.bundleId];
      if (!bundle) throw new Error(`No recorded bundle for ${scenario.bundleId}`);
      const loaded = bundleToLoadedReceipt(scenario.bundleId, bundle);
      const started = performance.now();
      const { report } = await runSerialized(() => runDemoVerification(loaded));
      const totalMs = performance.now() - started;
      const r = toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" });
      const matches = articleMatchesCredential(bundle);
      setResults((prev) => ({ ...prev, [scenario.bundleId]: r }));
      setContentMatches((prev) => ({ ...prev, [scenario.bundleId]: matches }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [scenario.bundleId]: err instanceof Error ? err.message : String(err)
      }));
    } finally {
      setBusy(false);
    }
  }

  const badge = !verified
    ? { cls: "cn-badge-idle", text: "verify to check" }
    : !result?.valid
      ? { cls: "cn-badge-bad", text: "verification failed" }
      : contentMatch === false
        ? { cls: "cn-badge-bad", text: "verified · story does not match" }
        : result?.conformant === false
          ? { cls: "cn-badge-bad", text: "verified · conformant: false" }
          : { cls: "cn-badge-ok", text: "verified ✓" };

  return (
    <section className="cn">
      <div className="cn-head-copy">
        <div className="cn-eyebrow">{config.eyebrow}</div>
        <h1 className="cn-headline">{config.headline}</h1>
        <p className="cn-sub">{config.sub}</p>
      </div>

      <div className="cn-story">
        <div className="cn-article">
          <div className="cn-kicker">{scenario.article.kicker}</div>
          <h2 className="cn-art-headline">{scenario.article.headline}</h2>
          <div className="cn-byline">
            {scenario.article.byline.lead} ·{" "}
            <span className={scenario.article.byline.midWarn ? "cn-byline-warn" : undefined}>
              {scenario.article.byline.mid}
            </span>
          </div>
          <div className="cn-lede">
            {scenario.article.lede.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        <div className="cn-bar">
          <div className="cn-badge">
            <span className="cn-chip" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l7 3v6c0 4-3 6.5-7 9-4-2.5-7-5-7-9V6z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </span>
            <span>
              Content Credentials <span className={"cn-badge-tag " + badge.cls}>{badge.text}</span>
            </span>
          </div>
          <button className="cn-verify-btn" onClick={verify} disabled={busy}>
            {busy ? config.verifyingLabel : verified ? config.reverifyLabel : config.verifyLabel}
          </button>
        </div>

        <div className="cn-cred-head">
          <div>
            <div className="cn-cred-title">{config.credTitle}</div>
            <div className="cn-cred-sub">{config.credSub}</div>
          </div>
          <div className="cn-mandate">
            <div className="mfield">
              <div className="mfield-k">{config.mandate.sourcesLabel}</div>
              <div className="mfield-v">{config.mandate.sources}</div>
            </div>
            <div className="mfield">
              <div className="mfield-k">{config.mandate.signoffLabel}</div>
              <div className="mfield-v">{config.mandate.signoff}</div>
            </div>
          </div>
        </div>

        <div className="cn-toggle" role="tablist" aria-label="Publish scenario">
          {config.scenarios.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={s.key === active}
              className={"cn-toggle-btn" + (s.key === active ? " cn-toggle-btn-active" : "")}
              onClick={() => setActive(s.key)}
              disabled={busy}
            >
              {s.toggleLabel}
            </button>
          ))}
        </div>

        <div className="cn-manifest">
          {scenario.steps.map((step, i) => (
            <div key={i} className={"cn-prov" + (step.flag ? " cn-prov-flagged" : "")}>
              <span className="cn-prov-ic" aria-hidden="true">
                {ICONS[step.icon]}
              </span>
              <div className="cn-prov-body">
                <div className="cn-prov-label">
                  {step.label}
                  {step.flag && (
                    <span className="cn-prov-warn" aria-hidden="true">
                      {" "}
                      ⚠
                    </span>
                  )}
                </div>
                <div className="cn-prov-detail">
                  {step.detail}
                  {step.flag && <span className="cn-prov-flag"> · {step.flag}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="cn-error">Verification error: {error}</div>}

        <CredentialVerdict scenario={scenario} result={result} contentMatch={contentMatch} />
      </div>

      <p className="cn-browser-note">{config.browserNote}</p>

      <div className="cn-ctas">
        {ctas.map((cta, i) => (
          <button className="cn-cta" key={i} onClick={cta.onClick}>
            <span className="cn-cta-label">{cta.label}</span>
            <span className="cn-cta-sub">{cta.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function CredentialVerdict({
  scenario,
  result,
  contentMatch
}: {
  scenario: NewsScenario;
  result: Result | null;
  // true/false once verified: does the displayed article's hash match the
  // credential's sealed content_hash? null before verification.
  contentMatch: boolean | null;
}) {
  if (!result) {
    return (
      <div className="cn-verdict cn-verdict-idle">
        <div className="cn-verdict-h">
          <span className="cn-verdict-mark" aria-hidden="true">
            •
          </span>
          <span className="cn-verdict-title">{scenario.summaryTitle}</span>
        </div>
        <p className="cn-verdict-detail">{scenario.summaryDetail}</p>
      </div>
    );
  }

  const valid = result.valid;
  const conformant = result.conformant;
  const cls = !valid
    ? "cn-verdict-invalid"
    : conformant === false
      ? "cn-verdict-warn"
      : "cn-verdict-valid";
  const title = !valid
    ? "Verification failed"
    : conformant === false
      ? "Credential verified: authentic, but NOT conformant"
      : "Credential verified: conformant";
  const detail = !valid
    ? `Rejected: ${result.reason ?? "see the failed check below."}`
    : conformant === false
      ? "Every step is sealed, witnessed, and authentic, but the credential honestly records that the story broke the editorial mandate (conformant: false). The credential caught it; it didn't hide it."
      : "Every step is sealed, witnessed, and bound to the editorial mandate. The sources are all on the allowlist and a human editor signed off before publishing. Anyone can verify the whole credential offline.";

  return (
    <div className={"cn-verdict " + cls}>
      <div className="cn-verdict-h">
        <span className="cn-verdict-mark" aria-hidden="true">
          {valid ? "✓" : "✕"}
        </span>
        <span className="cn-verdict-title">{title}</span>
      </div>
      <p className="cn-verdict-detail">{detail}</p>

      {valid && contentMatch === true && (
        <p className="cn-verdict-match">
          ✓ The story above is the exact content this credential authenticates. Its hash matches the
          sealed <code>content_hash</code>.
        </p>
      )}
      {valid && contentMatch === false && (
        <p className="cn-verdict-mismatch">
          ✕ The story shown does <strong>not</strong> match the credential's sealed{" "}
          <code>content_hash</code>. Treat the displayed text as unauthenticated.
        </p>
      )}

      {result.trustAnchorMode === "self" && (
        <p className="cn-verdict-anchor">
          Integrity self-check: verified against the witness key carried in this recorded receipt,
          not an independent third-party anchor. The <strong>Run live</strong> tab anchors to the
          witness's published key for a genuine third-party check.
        </p>
      )}

      <details className="cn-checks">
        <summary>Show the {result.checks.length} checks the verifier ran</summary>
        <ul className="cn-check-list">
          {result.checks.map((c) => (
            <li key={c.id} className={`cn-check cn-check-${c.status}`}>
              <span className="cn-check-mark" aria-hidden="true">
                {STATUS_MARK[c.status]}
              </span>
              <span className="cn-check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
